/**
 * Category taxonomy for the storefront catalog.
 *
 * Purpose: Single source of truth for the 4 shop categories derived from SKU
 *           patterns + the special "Build Your Own" pseudo-category. Pure
 *           functions only — no React, no I/O — so the contract is testable
 *           in isolation and reusable from any page/component.
 * Inputs:  Product (sku + product_type) or a raw SKU string.
 * Outputs: CategoryId + Category[] metadata + count/filter helpers.
 * Constraints:
 *   - NEVER change SKU patterns without updating parseCategorySlug.
 *   - 'byom' is a navigation pseudo-category, NOT a real product bucket —
 *     getProductCategory never returns 'byom'.
 *   - Sport: veterans/shop/src/lib/categories.ts · tests: __tests__/categories.test.ts
 */

import type { Product } from './types'

// ─── Types ────────────────────────────────────────────────────────────────────

export type CategoryId =
  | 'all'
  | 'branch-caps'
  | 'funny-caps'
  | 'gwot-army'
  | 'apparel'
  | 'byom'

export interface Category {
  id: CategoryId
  label: string
  /** Short description used on chips' title attribute + the empty-state copy. */
  hint: string
}

export interface CatalogFilterState {
  category: CategoryId
  query: string
}

// ─── Catalog metadata ─────────────────────────────────────────────────────────

/**
 * The 4 real shop categories (derived from SKU) + 'all' + the 'byom' link.
 * Order is the canonical chip order used on /products.
 */
export const CATEGORIES: Category[] = [
  { id: 'all',          label: 'All',                   hint: 'Every product in the shop.' },
  { id: 'branch-caps',  label: 'Branch Veteran Caps',   hint: 'Embroidered caps honoring each branch of service.' },
  { id: 'funny-caps',   label: 'Funny Caps',            hint: 'Branch pride with a sense of humor.' },
  { id: 'gwot-army',    label: 'GWOT & Army Heritage',  hint: 'Global War on Terror and classic Army designs.' },
  { id: 'apparel',      label: 'Apparel',               hint: 'Tees, long-sleeves, beanies, and hoodies.' },
  { id: 'byom',         label: 'Build Your Own',        hint: 'Design your own cap, tee, hoodie, or beanie.' },
]

/** Display-only metadata for the 4 real product categories (excludes 'all' + 'byom'). */
export const PRODUCT_CATEGORIES: Category[] = CATEGORIES.filter(
  (c) => c.id !== 'all' && c.id !== 'byom',
)

// ─── SKU → category parsing ───────────────────────────────────────────────────

/**
 * Apparel product_type values from scripts/seed-catalog.ts. Kept inline
 * (not imported) so this module stays pure-JS with no React/types circulars.
 */
const APPAREL_TYPES = new Set(['tshirt', 'longsleeve', 'beanie', 'hoodie'])

/**
 * Resolve a CategoryId from a raw SKU string. Pure & total — always returns
 * a value (never throws). Unknown SKUs fall back to 'other' implicitly via
 * the caller (countByCategory skips them).
 *
 * Patterns:
 *   VET-CAP-VET-<BR>-<SEQ>  → 'branch-caps'  (branch veteran caps line)
 *   VET-CAP-FNY-<BR>-<SEQ>  → 'funny-caps'   (funny branch caps line)
 *   VET-CAP-GWOT-<SEQ>      → 'gwot-army'
 *   VET-CAP-ARM-<SEQ>       → 'gwot-army'    (legacy single-composite Army line;
 *                                             VET-CAP-ARM-001 is hidden as a
 *                                             duplicate of VET-CAP-VET-ARM-001)
 *   VET-TEE-* / VET-LST-* / VET-BEAN-* / VET-HOOD-*  → 'apparel'
 */
export function parseCategorySlug(sku: string): CategoryId | null {
  const s = sku.toUpperCase()
  // Caps — match VET-CAP-VET / VET-CAP-FNY / VET-CAP-GWOT / VET-CAP-ARM
  // CRUCIAL: VET-CAP-VET-ARM-001 must hit 'branch-caps', not 'gwot-army'.
  // Test VET-CAP-VET-* and VET-CAP-FNY-* before VET-CAP-ARM-*.
  if (s.startsWith('VET-CAP-VET-')) return 'branch-caps'
  if (s.startsWith('VET-CAP-FNY-')) return 'funny-caps'
  if (s.startsWith('VET-CAP-GWOT-')) return 'gwot-army'
  if (s.startsWith('VET-CAP-ARM-')) return 'gwot-army'
  // Apparel — by SKU prefix (covers GWOT tee VET-TEE-002 etc.)
  if (
    s.startsWith('VET-TEE-') ||
    s.startsWith('VET-LST-') ||
    s.startsWith('VET-BEAN-') ||
    s.startsWith('VET-HOOD-')
  ) {
    return 'apparel'
  }
  return null
}

/**
 * Resolve the category for a Product. Falls back to the apparel check via
 * product_type so future apparel SKUs that don't follow the prefix pattern
 * still classify correctly. Returns null when the product doesn't fit any
 * known category.
 */
export function getProductCategory(product: Pick<Product, 'sku' | 'product_type'>): CategoryId | null {
  const fromSku = parseCategorySlug(product.sku)
  if (fromSku) return fromSku
  if (product.product_type && APPAREL_TYPES.has(product.product_type)) return 'apparel'
  return null
}

// ─── Aggregate helpers ────────────────────────────────────────────────────────

/**
 * Count products per category. 'all' is the total. Categories with 0 products
 * are still present in the result (callers render the chip but show "0").
 * 'byom' is NOT counted — it's a navigation link, not a bucket.
 */
export function countByCategory(products: Pick<Product, 'sku' | 'product_type'>[]): Record<Exclude<CategoryId, 'byom'>, number> {
  const counts: Record<Exclude<CategoryId, 'byom'>, number> = {
    all: products.length,
    'branch-caps': 0,
    'funny-caps': 0,
    'gwot-army': 0,
    apparel: 0,
  }
  for (const p of products) {
    const c = getProductCategory(p)
    if (c && c !== 'byom') counts[c]++
  }
  return counts
}

/**
 * Filter products by category. 'all' returns the input unchanged. 'byom' is
 * treated as 'all' here — the chip itself does the /design navigation; if a
 * caller passes 'byom' to this filter we hand back everything so the grid
 * never goes empty by accident.
 */
export function filterByCategory(
  products: Product[],
  category: CategoryId,
): Product[] {
  if (category === 'all' || category === 'byom') return products
  return products.filter((p) => getProductCategory(p) === category)
}

/**
 * Normalize the shareable product search query. The UI stores the normalized
 * value in ?q= so copied links and browser history stay deterministic.
 */
export function normalizeSearchQuery(raw: string | null | undefined): string {
  return (raw ?? '').trim().replace(/\s+/g, ' ')
}

/** Read the /products filter state from URLSearchParams (?c=...&q=...). */
export function catalogFilterStateFromParams(params: URLSearchParams): CatalogFilterState {
  return {
    category: normalizeCategoryParam(params.get('c')),
    query: normalizeSearchQuery(params.get('q')),
  }
}

/**
 * Build the next /products URLSearchParams from an existing query string.
 * Preserves unrelated params such as sort, but removes legacy branch/type
 * params once the new c/q filter contract is used.
 */
export function updateCatalogSearchParams(
  current: URLSearchParams,
  patch: Partial<CatalogFilterState>,
): URLSearchParams {
  const next = new URLSearchParams(current)

  if (patch.category !== undefined) {
    const category = normalizeCategoryParam(patch.category)
    if (category === 'all') {
      next.delete('c')
    } else {
      next.set('c', category)
    }
  }

  if (patch.query !== undefined) {
    const query = normalizeSearchQuery(patch.query)
    if (query) {
      next.set('q', query)
    } else {
      next.delete('q')
    }
  }

  next.delete('branch')
  next.delete('type')
  return next
}

/** Name-only product search used by /products. */
export function filterBySearch(products: Product[], query: string): Product[] {
  const needle = normalizeSearchQuery(query).toLowerCase()
  if (!needle) return products
  return products.filter((p) => p.name.toLowerCase().includes(needle))
}

/** Apply category + name query filters client-side; no network refetch. */
export function filterCatalogProducts(
  products: Product[],
  filters: CatalogFilterState,
): Product[] {
  return filterBySearch(filterByCategory(products, filters.category), filters.query)
}

/**
 * Counts for filter chips. Counts honor the current text search so a shopper
 * can see which categories still have matches for ?q=.
 */
export function countCatalogChips(
  products: Product[],
  query: string,
): Record<Exclude<CategoryId, 'byom'>, number> {
  return countByCategory(filterBySearch(products, query))
}

/**
 * Validate + normalize an arbitrary URL-param string into a CategoryId.
 * Returns 'all' for missing/unknown values — never throws.
 */
export function normalizeCategoryParam(raw: string | null | undefined): CategoryId {
  if (!raw) return 'all'
  const known = new Set<CategoryId>(['all', 'branch-caps', 'funny-caps', 'gwot-army', 'apparel', 'byom'])
  return known.has(raw as CategoryId) ? (raw as CategoryId) : 'all'
}
